import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Building2, Briefcase, Eye, X, FolderKanban, Plus, Edit, Users } from 'lucide-react';
import { HeaderSkeleton, CardGridSkeleton } from './skeletons/Skeletons';
import EmptyState from './EmptyState';

const BACKEND_URL = '';

// Create axios instance for employee
const employeeApi = axios.create({
  baseURL: BACKEND_URL,
  timeout: 30000,
});

employeeApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('employee_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default function EmployeeClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showClientModal, setShowClientModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: '', category: '', industry: '', brand_color: '#000000',
    contacts: [{ name: '', email: '', phone: '', designation: '' }],
    billing_contacts: [{ name: '', email: '', role: '' }],
    social_media: { instagram: '', facebook: '', twitter: '', linkedin: '', youtube: '', website: '' }
  });

  useEffect(() => {
    fetchClients();
    // REMOVED: checkBossDepartment() - Boss privileges eliminated
    fetchDepartments();
    fetchEmployees();
  }, []);

  // REMOVED: checkBossDepartment function - Boss privileges eliminated

  const fetchDepartments = async () => {
    try {
      const response = await employeeApi.get('/api/employee/departments');
      setDepartments(response.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeeApi.get('/api/employee/team');
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await employeeApi.get('/api/employee/clients');
      setClients(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
      setLoading(false);
    }
  };

  const handleViewDetails = (client) => {
    // Route to the standalone detail page (ClientDetailPage) which now hosts
    // the POC editor + "Add Department" flow. Employees always get the full
    // POC-management UI — never the old read-only modal.
    navigate(`/employee/clients/${client._id}`);
  };

  const handleCreateClient = () => {
    setIsEditing(false);
    setClientForm({
      name: '',
      company_name: '',
      email: '',
      phone: '',
      company_address: '',
      gstin: '',
      pan: '',
      retainer_value: 0,
      category: 'Standard',
      departments: [],
      pocs: {},
      brand_color: '#000000',
      billing_cycle: 'Monthly',
      payment_type: 'Prepaid',
      retainer_start: '',
      retainer_end: '',
      industry: ''
    });
    setActiveTab('overview');
    setShowClientModal(true);
  };

  const handleEditClient = async (client) => {
    try {
      const response = await employeeApi.get(`/api/clients/${client._id}/details`);
      const fullClient = response.data.client || client;
      
      setIsEditing(true);
      setSelectedClient(client);
      setClientForm({
        name: fullClient.name || '',
        company_name: fullClient.company_name || '',
        email: fullClient.email || '',
        phone: fullClient.phone || '',
        company_address: fullClient.company_address || '',
        gstin: fullClient.gstin || '',
        pan: fullClient.pan || '',
        retainer_value: fullClient.retainer_value || fullClient.retainer || 0,
        category: fullClient.category || 'Standard',
        departments: fullClient.departments || [],
        pocs: fullClient.pocs || {},
        brand_color: fullClient.brand_color || '#000000',
        billing_cycle: fullClient.billing_cycle || 'Monthly',
        payment_type: fullClient.payment_type || 'Prepaid',
        retainer_start: fullClient.retainer_start || '',
        retainer_end: fullClient.retainer_end || '',
        industry: fullClient.industry || ''
      });
      setActiveTab('overview');
      setShowClientModal(true);
    } catch (error) {
      toast.error('Failed to load client for editing');
      console.error('Error:', error);
    }
  };

  const handleSubmitClient = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await employeeApi.put(`/api/employee/clients/${selectedClient._id}`, clientForm);
        toast.success('Client updated successfully');
      } else {
        await employeeApi.post('/api/employee/clients', clientForm);
        toast.success('Client created successfully');
      }
      setShowClientModal(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
      console.error('Error:', error);
    }
  };

  const handleDepartmentToggle = (deptName) => {
    setClientForm(prev => {
      const departments = prev.departments.includes(deptName)
        ? prev.departments.filter(d => d !== deptName)
        : [...prev.departments, deptName];
      return { ...prev, departments };
    });
  };

  const handlePocChange = (deptName, employeeId) => {
    setClientForm(prev => ({
      ...prev,
      pocs: { ...prev.pocs, [deptName]: employeeId }
    }));
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-8 space-y-6" data-testid="employee-clients-loading">
        <HeaderSkeleton />
        <CardGridSkeleton count={6} columns={3} />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]" data-testid="employee-clients-heading">Clients</h1>
          <p className="text-neutral-600 mt-1">
            View all active clients
          </p>
        </div>
        {/* REMOVED: Add Client button - Now admin-only (Boss privileges eliminated) */}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            data-testid="search-clients-input"
          />
        </div>
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="glass-card">
          <EmptyState
            icon={Building2}
            title={searchQuery ? 'No clients match your search' : 'No clients available'}
            description={searchQuery ? 'Try a different search term.' : 'Check back later for updates.'}
            size="large"
            testId="empty-employee-clients"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <div 
              key={client._id} 
              className="glass-card p-6 hover:shadow-lg transition-shadow"
              data-testid={`client-card-${client._id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#1a1a2e]">{client.name}</h3>
                  <p className="text-sm text-neutral-600 mt-1">{client.category || 'No Category'}</p>
                </div>
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: client.brand_color ? `${client.brand_color}20` : '#fef3c7' }}
                >
                  <Building2 className="w-5 h-5" style={{ color: client.brand_color || '#d97706' }} />
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Briefcase className="w-4 h-4" />
                  <span>{client.task_count || 0} Tasks</span>
                </div>
                {client.departments && client.departments.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-neutral-600">
                    <FolderKanban className="w-4 h-4" />
                    <span>{client.departments.length} Departments</span>
                  </div>
                )}
                {client.departments && client.departments.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {client.departments.slice(0, 3).map((dept) => (
                      <span key={dept} className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                        {dept}
                      </span>
                    ))}
                    {client.departments.length > 3 && (
                      <span className="px-2 py-1 bg-neutral-100 text-neutral-600 rounded text-xs">
                        +{client.departments.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleViewDetails(client)}
                  className="flex-1 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-xl hover:bg-neutral-200 text-sm font-medium flex items-center justify-center gap-2"
                  data-testid={`view-details-${client._id}`}
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
                {/* REMOVED: Edit button - Now admin-only (Boss privileges eliminated) */}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Client Details Modal (View Only) */}
      {showDetailsModal && clientDetails && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-white/40 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-[#1a1a2e]">{selectedClient.name}</h2>
                <p className="text-sm text-neutral-600">{selectedClient.category}</p>
              </div>
              <button onClick={() => setShowDetailsModal(false)} className="text-neutral-500 hover:text-neutral-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Client Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm text-neutral-600">Total Tasks</p>
                  <p className="text-2xl font-bold text-blue-600">{clientDetails.task_count || 0}</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4">
                  <p className="text-sm text-neutral-600">Departments</p>
                  <p className="text-2xl font-bold text-purple-600">{selectedClient.departments?.length || 0}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-sm text-neutral-600">POCs</p>
                  <p className="text-2xl font-bold text-green-600">{clientDetails.pocs?.length || 0}</p>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4">
                  <p className="text-sm text-neutral-600">Industry</p>
                  <p className="text-lg font-bold text-yellow-600">{selectedClient.industry || 'N/A'}</p>
                </div>
              </div>

              {/* Company Info - View-only for all employees */}
              {clientDetails.client && (
                <div className="mb-8 p-4 bg-white/40 rounded-xl">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Company Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {clientDetails.client.company_name && (
                      <div>
                        <p className="text-neutral-500">Company Name</p>
                        <p className="font-medium">{clientDetails.client.company_name}</p>
                      </div>
                    )}
                    {clientDetails.client.gstin && (
                      <div>
                        <p className="text-neutral-500">GSTIN</p>
                        <p className="font-medium">{clientDetails.client.gstin}</p>
                      </div>
                    )}
                    {clientDetails.client.pan && (
                      <div>
                        <p className="text-neutral-500">PAN</p>
                        <p className="font-medium">{clientDetails.client.pan}</p>
                      </div>
                    )}
                    {clientDetails.client.email && (
                      <div>
                        <p className="text-neutral-500">Email</p>
                        <p className="font-medium">{clientDetails.client.email}</p>
                      </div>
                    )}
                    {clientDetails.client.phone && (
                      <div>
                        <p className="text-neutral-500">Phone</p>
                        <p className="font-medium">{clientDetails.client.phone}</p>
                      </div>
                    )}
                    {clientDetails.client.company_address && (
                      <div className="col-span-2">
                        <p className="text-neutral-500">Address</p>
                        <p className="font-medium">{clientDetails.client.company_address}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Departments List */}
              {selectedClient.departments && selectedClient.departments.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Active Departments</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedClient.departments.map((dept) => (
                      <span key={dept} className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-xl text-sm font-medium">
                        {dept}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* POCs */}
              {clientDetails.pocs && clientDetails.pocs.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Points of Contact</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {clientDetails.pocs.map((poc, idx) => (
                      <div key={idx} className="bg-white/40 rounded-xl p-4">
                        <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">{poc.department}</p>
                        <p className="font-semibold text-[#1a1a2e]">{poc.name}</p>
                        <p className="text-sm text-neutral-600">{poc.email}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Tasks */}
              {clientDetails.tasks && clientDetails.tasks.length > 0 && (
                <div>
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Recent Tasks</h3>
                  <div className="space-y-3">
                    {clientDetails.tasks.slice(0, 5).map((task) => (
                      <div key={task._id} className="bg-white/40 rounded-xl p-4 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-[#1a1a2e]">{task.title}</p>
                          <p className="text-sm text-neutral-600">{task.department} • {task.task_type}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          task.status === 'Completed' ? 'bg-green-100 text-green-700' :
                          task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Client Modal */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-white/40 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-[#1a1a2e]">
                {isEditing ? 'Edit Client' : 'Create New Client'}
              </h2>
              <button onClick={() => setShowClientModal(false)} className="text-neutral-500 hover:text-neutral-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b border-white/40 px-6">
              <nav className="flex gap-8">
                {['overview', 'company', 'departments'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 border-b-2 font-medium text-sm capitalize transition-colors ${
                      activeTab === tab 
                        ? 'border-yellow-500 text-yellow-600' 
                        : 'border-transparent text-neutral-500 hover:text-neutral-700'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            <form onSubmit={handleSubmitClient} className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Client Name *</label>
                      <input
                        type="text"
                        value={clientForm.name}
                        onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        required
                        data-testid="client-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Category</label>
                      <select
                        value={clientForm.category}
                        onChange={(e) => setClientForm({ ...clientForm, category: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      >
                        <option value="Standard">Standard</option>
                        <option value="Premium">Premium</option>
                        <option value="Enterprise">Enterprise</option>
                        <option value="Startup">Startup</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Industry</label>
                      <input
                        type="text"
                        value={clientForm.industry}
                        onChange={(e) => setClientForm({ ...clientForm, industry: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        placeholder="e.g., Technology, Healthcare"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Brand Color</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={clientForm.brand_color}
                          onChange={(e) => setClientForm({ ...clientForm, brand_color: e.target.value })}
                          className="w-12 h-10 border border-white/40 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={clientForm.brand_color}
                          onChange={(e) => setClientForm({ ...clientForm, brand_color: e.target.value })}
                          className="flex-1 px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={clientForm.email}
                        onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Phone</label>
                      <input
                        type="text"
                        value={clientForm.phone}
                        onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Retainer Value</label>
                      <input
                        type="number"
                        value={clientForm.retainer_value}
                        onChange={(e) => setClientForm({ ...clientForm, retainer_value: parseFloat(e.target.value) || 0 })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Billing Cycle</label>
                      <select
                        value={clientForm.billing_cycle}
                        onChange={(e) => setClientForm({ ...clientForm, billing_cycle: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      >
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                        <option value="Yearly">Yearly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Payment Type</label>
                      <select
                        value={clientForm.payment_type}
                        onChange={(e) => setClientForm({ ...clientForm, payment_type: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      >
                        <option value="Prepaid">Prepaid</option>
                        <option value="Postpaid">Postpaid</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Retainer Start Date</label>
                      <input
                        type="date"
                        value={clientForm.retainer_start}
                        onChange={(e) => setClientForm({ ...clientForm, retainer_start: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">Retainer End Date</label>
                      <input
                        type="date"
                        value={clientForm.retainer_end}
                        onChange={(e) => setClientForm({ ...clientForm, retainer_end: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Company Tab */}
              {activeTab === 'company' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      value={clientForm.company_name}
                      onChange={(e) => setClientForm({ ...clientForm, company_name: e.target.value })}
                      className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">GSTIN</label>
                      <input
                        type="text"
                        value={clientForm.gstin}
                        onChange={(e) => setClientForm({ ...clientForm, gstin: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        placeholder="22AAAAA0000A1Z5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">PAN</label>
                      <input
                        type="text"
                        value={clientForm.pan}
                        onChange={(e) => setClientForm({ ...clientForm, pan: e.target.value })}
                        className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        placeholder="AAAAA0000A"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Company Address</label>
                    <textarea
                      value={clientForm.company_address}
                      onChange={(e) => setClientForm({ ...clientForm, company_address: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    />
                  </div>
                </div>
              )}

              {/* Departments Tab */}
              {activeTab === 'departments' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-4">Select Departments</label>
                    <div className="grid grid-cols-3 gap-3">
                      {departments.map(dept => (
                        <label 
                          key={dept._id || dept.name} 
                          className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${
                            clientForm.departments.includes(dept.name)
                              ? 'bg-yellow-50 border-yellow-500'
                              : 'bg-white border-white/40 hover:border-white/40'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={clientForm.departments.includes(dept.name)}
                            onChange={() => handleDepartmentToggle(dept.name)}
                            className="w-4 h-4 text-yellow-600 border-white/40 rounded focus:ring-yellow-500"
                          />
                          <span className="text-sm font-medium text-neutral-700">{dept.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {clientForm.departments.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-4">Assign POCs</label>
                      <div className="space-y-4">
                        {clientForm.departments.map(deptName => (
                          <div key={deptName} className="flex items-center gap-4 p-4 bg-white/40 rounded-xl">
                            <div className="w-32">
                              <span className="text-sm font-medium text-[#1a1a2e]">{deptName}</span>
                            </div>
                            <select
                              value={clientForm.pocs[deptName] || ''}
                              onChange={(e) => handlePocChange(deptName, e.target.value)}
                              className="flex-1 px-4 py-2 border border-white/40 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                            >
                              <option value="">Select POC</option>
                              {employees.map(emp => (
                                <option key={emp._id} value={emp._id}>
                                  {emp.personal?.full_name || emp.work_email}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Form Actions */}
              <div className="mt-8 flex justify-end gap-4 pt-6 border-t border-white/40">
                <button
                  type="button"
                  onClick={() => setShowClientModal(false)}
                  className="px-6 py-2 border border-white/40 text-neutral-700 rounded-xl hover:bg-white/60"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-500 text-white rounded-xl hover:bg-yellow-600 font-medium"
                  data-testid="save-client-btn"
                >
                  {isEditing ? 'Update Client' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
